import { Mic, MicOff, Phone } from "lucide-react";
import { LiveAudioVisualizer } from "react-audio-visualize";
import ImageWithBlob from "./animation/ImageWithBlob";
interface VoiceCallInterfaceProps {
  isCalling: boolean;
  isMuted: boolean;
  setIsMuted: (muted: boolean) => void;
  agentName: string;
  toggleMute: () => void;
  onEndCall: () => void;
  onChat: () => void;
  mediaRecorder: MediaRecorder | null;
  icon?: string;
  seconds?: number;
  callEnded?: boolean;
}

const formatTimerTime = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const VoiceCallInterface: React.FC<VoiceCallInterfaceProps> = ({
  isCalling,
  isMuted,
  setIsMuted,
  toggleMute,
  agentName,
  onEndCall,
  onChat,
  mediaRecorder,
  icon,
  seconds,
  callEnded
}) => {
  return (
    <>
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      <div className="bg-blue-950/40 p-0 rounded-[30px] sm:rounded-[48px]">
        <div
          className="m-4 sm:m-4 sm:pt-6 sm:pb-8 w-[80vw] md:w-[350px] h-[50vh] sm:h-[80vh] min-h-[400px]  text-white flex flex-col relative overflow-hidden rounded-[2rem] sm:rounded-[3rem] before:absolute before:inset-[-6px] sm:before:inset-[-9px] before:border-[6px] sm:before:border-[9px] before:border-[#FFFFFF0D] before:rounded-[2rem] sm:before:rounded-[3rem] before:pointer-events-none before:bg-transparent"
          style={{
            background:
              "linear-gradient(220deg,#0d1b3d 0%, #1a237e 25%, #3949ab 50%, #42a5f5 75%,rgb(138, 217, 254) 100%)",
            fontFamily: "Manrope, sans-serif"
          }}
        >
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-12 left-6 w-24 h-24 bg-purple-500 rounded-full blur-2xl"></div>
            <div className="absolute bottom-28 right-6 w-32 h-32 bg-pink-500 rounded-full blur-2xl"></div>
          </div>

          {/* Header */}
          <div className="flex-shrink-0 pt-4 sm:pt-6 text-center z-10 mb-1 sm:mb-2">
            <h1
              className="text-base sm:text-lg md:text-xl font-medium text-white/90"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {agentName}
            </h1>
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col items-center justify-center relative z-10 min-h-0 py-2">
            <div className="relative mb-2 sm:mb-4 md:mb-8">
              <ImageWithBlob imageSrc={icon} />
            </div>

            {/* Mute indicator */}
            <div className="text-center mb-2 sm:mb-4 md:mb-8">
              <p className="text-sm sm:text-lg md:text-xl text-white/80 font-light tracking-wide">
                {callEnded
                  ? "Call has ended"
                  : isCalling
                  ? isMuted
                    ? "Microphone muted"
                    : "Listening..."
                  : "Inactive"}
              </p>
            </div>
          </div>

          {/* Timer - Centered below audio visualizer */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="text-sm sm:text-base md:text-lg font-medium text-white/90 tracking-wide">
              {formatTimerTime(seconds ?? 0)}
            </div>
          </div>

          {/* Controls - Fixed at bottom with proper spacing */}
          <div className="flex-shrink-0 pb-3 sm:pb-4 md:pb-8 px-4 sm:px-8 z-10">
            {/* Waveform */}
            <div className="sound-visualizer-wrapper mb-3 sm:mb-4 flex justify-center">
              {mediaRecorder && !isMuted && isCalling ? (
                <div style={{ display: "flex", gap: "3px" }}>
                  <div style={{ transform: "scaleX(-1)" }}>
                    <LiveAudioVisualizer
                      mediaRecorder={mediaRecorder}
                      width={80}
                      height={50}
                      barWidth={2}
                      gap={2}
                      barColor={"#ffffff"}
                      fftSize={256}
                      minDecibels={-70}
                      smoothingTimeConstant={0.9}
                    />
                  </div>
                  <div>
                    <LiveAudioVisualizer
                      mediaRecorder={mediaRecorder}
                      width={120}
                      height={50}
                      barWidth={2}
                      gap={2}
                      barColor={"#ffffff"}
                      fftSize={256}
                      minDecibels={-70}
                      smoothingTimeConstant={0.9}
                    />
                  </div>
                </div>
              ) : (
                // Placeholder bars when muted or no mediaRecorder
                <div
                  style={{
                    width: "203px",
                    height: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: 0.3
                  }}
                >
                  <div
                    style={{ display: "flex", gap: "2px", alignItems: "end" }}
                  >
                    {Array.from({ length: 30 }, (_, i) => (
                      <div
                        key={i}
                        style={{
                          width: "2px",
                          height: "8px",
                          backgroundColor: "#ffffff",
                          opacity: 0.3
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-center space-x-4 sm:space-x-8">
              {/* Mute button */}
              <button
                onClick={toggleMute}
                className={`flex items-center justify-center gap-5 px-4 py-2 rounded-[44px] border-[0.5px] transition-all duration-300 md:w-[55px] md:h-[50px] hover:scale-110 hover:shadow-lg ${
                  isMuted ? "border-red-300" : "border-[#F3F4F6]"
                }`}
                style={{
                  width: "55px",
                  height: "40px",
                  background: isMuted
                    ? "linear-gradient(180deg, #FEE2E2 0%, #FECACA 32.51%, #FCA5A5 100%)"
                    : "linear-gradient(180deg, #F9FAFB 0%, #F3F4F6 32.51%, #D1D5DB 100%)",
                  boxShadow: "0px 12px 16px 0px #20202033"
                }}
              >
                {isMuted ? (
                  <MicOff size={20} className="text-red-500" />
                ) : (
                  <Mic size={20} className="text-gray-500" />
                )}
              </button>
              {/* End call button */}
              <button
                onClick={onEndCall}
                className="flex items-center justify-center gap-5 px-4 py-2 rounded-[44px] transition-all duration-300 hover:scale-110 hover:shadow-lg"
                style={{
                  width: "55px",
                  height: "40px",
                  background:
                    "linear-gradient(180deg, #F87171 0%, #EF4444 32.51%, #DC2626 100%)",
                  boxShadow: "0px 12px 16px 0px #20202033"
                }}
              >
                <Phone size={20} className="text-white rotate-[135deg]" />
              </button>
            </div>
          </div>

          <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    </>
  );
};

export default VoiceCallInterface;
